/**
 * SimulatorResults Component - UI Layer
 * Displays simulation summary cards and amortization schedule table
 */

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import type { SimulatorResponse } from '@/types/policy.types';

interface SimulatorResultsProps {
  result: SimulatorResponse;
  onReset: () => void;
}

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);

const formatPercent = (value: number): string =>
  `${value.toFixed(2)}%`;

interface SummaryCardProps {
  label: string;
  value: string;
}

function SummaryCard({ label, value }: SummaryCardProps) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold text-foreground">{value}</p>
    </div>
  );
}

export function SimulatorResults({ result, onReset }: SimulatorResultsProps) {
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <Card title="Resumen de Simulacion">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <SummaryCard label="Monto" value={formatCurrency(result.amount)} />
          <SummaryCard label="Pago Mensual" value={formatCurrency(result.monthlyPayment)} />
          <SummaryCard label="Total a Pagar" value={formatCurrency(result.totalPayment)} />
          <SummaryCard label="Interes Total" value={formatCurrency(result.totalInterest)} />
          <SummaryCard label="Tasa Anual" value={formatPercent(result.interestRate)} />
        </div>
      </Card>

      {/* Amortization Table */}
      <Card title="Tabla de Amortizacion">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-3 py-2 font-medium text-muted-foreground">#</th>
                <th className="px-3 py-2 font-medium text-muted-foreground">Fecha</th>
                <th className="px-3 py-2 text-right font-medium text-muted-foreground">Capital</th>
                <th className="px-3 py-2 text-right font-medium text-muted-foreground">Interes</th>
                <th className="px-3 py-2 text-right font-medium text-muted-foreground">Pago Total</th>
                <th className="px-3 py-2 text-right font-medium text-muted-foreground">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {(result.schedule ?? []).map((item) => (
                <tr key={item.number} className="border-b border-border/50 hover:bg-muted/50">
                  <td className="px-3 py-2 text-foreground">{item.number}</td>
                  <td className="px-3 py-2 text-foreground">{item.dueDate}</td>
                  <td className="px-3 py-2 text-right text-foreground">
                    {formatCurrency(item.principal)}
                  </td>
                  <td className="px-3 py-2 text-right text-foreground">
                    {formatCurrency(item.interest)}
                  </td>
                  <td className="px-3 py-2 text-right font-medium text-foreground">
                    {formatCurrency(item.totalPayment)}
                  </td>
                  <td className="px-3 py-2 text-right text-foreground">
                    {formatCurrency(item.balance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Reset Button */}
      <div className="flex justify-end">
        <Button variant="outline" onClick={onReset}>
          Nueva Simulacion
        </Button>
      </div>
    </div>
  );
}
